import { Static, Type } from '@sinclair/typebox';
import deepEqual from 'deep-equal';

import {
  AI_BUILDER_CARD_VISIBLE_OPTIONS,
  AI_BUILDER_SIGNALS,
  AiBuilderCard,
  AiBuilderSystemMessage,
  CardDropdownOption,
  EXTERNAL_DATA_MARKERS,
  FlowVersion,
  isNil,
  NavigateDirective,
} from '@activepieces/shared';

import { AVAILABLE_PIECES_TRANSLATE } from '../../../available-pieces-translate';

// The chat view-model — not @activepieces/shared's AiBuilderMessage, the
// {role, content} wire shape. Lives here, NOT in chat-message.tsx: that module
// pulls react-markdown (pure ESM, unparseable by Jest), so anything a test
// imports — the store, the turn hook — must reach this type without touching it.
export const AiBuilderChatMessage = Type.Union([
  Type.Object({
    userType: Type.Union([Type.Literal('user'), Type.Literal('bot')]),
    content: Type.String(),
    contentParams: Type.Optional(Type.Record(Type.String(), Type.String())),
    contentPrefix: Type.Optional(Type.String()),
    previousFlowVersion: Type.Optional(Type.Any()),
    newFlowVersion: Type.Optional(Type.Any()),
    suggestedActions: Type.Optional(
      Type.Array(
        Type.Object({
          label: Type.String(),
          message: Type.String(),
        }),
      ),
    ),
    card: Type.Optional(AiBuilderCard),
    answeredChoice: Type.Optional(Type.String()),
    sourcePrompt: Type.Optional(Type.String()),
    flowReady: Type.Optional(Type.Boolean()),
    /** A line the assistant reports mid-turn — never the turn's answer */
    narration: Type.Optional(Type.Boolean()),
    navigateDirectives: Type.Optional(Type.Array(NavigateDirective)),
  }),
]);
export type AiBuilderChatMessage = Static<typeof AiBuilderChatMessage>;

const TRIGGER_STEP_NAME = 'trigger';

/** Field-name lists arrive bolded and comma-joined: "**Subject**, **Body**" */
const BOLD_FIELD_NAME = /\*\*([^*]+)\*\*/g;

/**
 * Translates each field name of a bolded, comma-joined list through the
 * piece's namespace. The whole list is never a Tolgee key, and neither is a
 * name wrapped in asterisks — which is why field names stayed English while
 * step names (passed unwrapped) were translated.
 */
export const translateFieldNameList = (
  value: string,
  translate: (name: string) => string,
): string =>
  value.replaceAll(BOLD_FIELD_NAME, (match, name: string) => {
    const translated = translate(name.trim());
    return `**${translated}**`;
  });

// When a key is missing from the Tolgee catalog, i18next returns it verbatim
// without ICU formatting — fill the {param} placeholders ourselves so the user
// sees values instead of raw braces.
export const interpolateMissingIcuParams = (
  text: string,
  params: Record<string, string>,
): string =>
  text.replaceAll(/\{(\w+)\}/g, (match, paramName: string) =>
    paramName in params ? params[paramName] : match,
  );

/** The step as the CANVAS draws it: which steps, of which kind, in which order */
const extractShapeSignature = (step: Record<string, unknown>) => ({
  name: step.name,
  type: step.type,
  displayName: step.displayName,
});

/** The same, plus what each step was configured WITH */
const extractContentSignature = (step: Record<string, unknown>) => ({
  ...extractShapeSignature(step),
  input: (step.settings as Record<string, unknown>)?.input,
});

type FlowStructure = {
  triggers: Record<string, unknown>[];
  actions: Record<string, unknown>[];
  connections: unknown[];
};

const differs = (
  previous: FlowStructure,
  next: FlowStructure,
  signature: (step: Record<string, unknown>) => unknown,
): boolean =>
  !deepEqual(next.triggers.map(signature), previous.triggers.map(signature)) ||
  !deepEqual(next.actions.map(signature), previous.actions.map(signature)) ||
  !deepEqual(next.connections, previous.connections);

/**
 * Whether the turn changed the flow's SHAPE — steps added, removed, renamed,
 * reordered. Drives how the answer is applied: a shape change rebuilds and
 * re-lays out the canvas, while values alone take the diff-aware merge. Values
 * must NOT count here: on a configure turn the server version is identical to
 * (or staler than) what the user edits in the panel, and replacing it resets
 * the open form — which the panel's autosave then persists.
 */
export const hasFlowStructureChanged = (
  previous: FlowStructure,
  next: FlowStructure,
): boolean => differs(previous, next, extractShapeSignature);

/**
 * Whether the turn changed ANYTHING in the flow, values included. Drives the
 * undo affordance: filling a field is exactly what a user may want to revert.
 */
export const hasFlowChanged = (
  previous: FlowStructure,
  next: FlowStructure,
): boolean => differs(previous, next, extractContentSignature);

const asSignalValue = (value: unknown): string =>
  typeof value === 'string' ? value : '';

type BuildHelpMessageParams = {
  flowVersion: FlowVersion;
  sampleData: Record<string, unknown>;
  stepName: string;
};

/**
 * Payload for a "help me with this step" request. Pure on purpose: the effect
 * that sends it stays trivial, and the external-data tagging below — the part
 * that keeps a test result from reading as instructions to the agent — is
 * testable on its own.
 */
/**
 * Past this, the sample is left out of the help signal. The server truncates
 * what it feeds the model anyway, so a bigger payload buys nothing — and this
 * string is persisted with the conversation.
 */
const MAX_HELP_SAMPLE_LENGTH = 4000;

export const buildHelpMessage = ({
  flowVersion,
  sampleData,
  stepName,
}: BuildHelpMessageParams): string => {
  const step =
    stepName === TRIGGER_STEP_NAME
      ? flowVersion.triggers[0]
      : flowVersion.actions.find((action) => action.name === stepName);
  const settings = step?.settings as Record<string, unknown> | undefined;
  // The server parses this line field by field: a non-string value reaching it
  // would produce a malformed signal, so anything else reads as absent.
  const pieceName = asSignalValue(settings?.pieceName);
  const actionOrTriggerName =
    asSignalValue(settings?.actionName) || asSignalValue(settings?.triggerName);

  const message = `${AI_BUILDER_SIGNALS.HELP_STEP} stepName=${stepName} piece=${pieceName} action=${actionOrTriggerName}`;

  const stepSampleData = sampleData[stepName];
  if (stepSampleData === undefined) {
    return message;
  }
  const serialised = JSON.stringify(stepSampleData);
  // An oversized sample is DROPPED, never cut: this prompt is replayed
  // verbatim by retry, and a JSON severed mid-object between the external-data
  // markers would reach the model as corrupt data. The server reads the step's
  // persisted sample itself when the line is absent — and it is also what
  // keeps this string, stored in the chat history, from filling localStorage.
  if (serialised.length > MAX_HELP_SAMPLE_LENGTH) {
    return message;
  }
  return `${message}\nlastTestResult=${EXTERNAL_DATA_MARKERS.START}${serialised}${EXTERNAL_DATA_MARKERS.END}`;
};

type CardChoiceOption = { label: string; value: unknown };

export type CardSelectChoices = {
  cardId: string;
  /** What is being chosen — feeds the select placeholder */
  subject: string;
  /** The whole list, reached by search */
  options: CardChoiceOption[];
  /** Shown before any search: what matches the target the user named */
  featured: CardChoiceOption[];
};

/** The piece's Tolgee namespace, when it ships translations */
export const pieceNamespaceFor = (
  pieceName: string | undefined,
): string | undefined =>
  pieceName && AVAILABLE_PIECES_TRANSLATE.includes(pieceName)
    ? pieceName
    : undefined;

/**
 * The card's subject is a prop label from the piece metadata: translated
 * through the piece's namespace, like the sentence right above the card.
 */
export const localiseCardSubject = (
  choices: CardSelectChoices | null,
  pieceName: string | undefined,
  translate: (key: string, ns?: string) => string,
): CardSelectChoices | null => {
  if (isNil(choices)) return null;
  const ns = pieceNamespaceFor(pieceName);
  return ns ? { ...choices, subject: translate(choices.subject, ns) } : choices;
};

/**
 * The card types the chat renders as a real select: any unbounded list of
 * values. Short fixed choices (write preview, zero-connection gate) stay
 * quick-reply buttons — one click beats two.
 */
export const toCardSelectChoices = (
  card: AiBuilderCard | undefined,
): CardSelectChoices | null => {
  if (isNil(card)) return null;
  if (card.type === 'dropdown_choice') {
    const toChoice = (option: CardDropdownOption) => ({
      label: String(option.label),
      value: option.value,
    });
    return {
      cardId: card.cardId,
      subject: card.propDisplayName,
      options: card.allOptions.map(toChoice),
      featured: card.shortlist.map(toChoice),
    };
  }
  if (card.type === 'connection_gate' && card.connections.length > 1) {
    const options = card.connections.map((connection) => ({
      label: connection.displayName,
      value: connection.externalId,
    }));
    return {
      cardId: card.cardId,
      subject: card.pieceDisplayName,
      options,
      featured: options,
    };
  }
  return null;
};

// Combining marks left behind by NFD normalization
const DIACRITICS = /[\u0300-\u036F]/g;

const normalizeForSearch = (text: string): string =>
  text.toLowerCase().normalize('NFD').replaceAll(DIACRITICS, '');

/**
 * What the card lists for a search: a Slack workspace returns up to 2000
 * channels, so the card never lists more than a handful — the shortlist
 * before any search, the first matches of the WHOLE list after.
 */
export const visibleCardChoices = (
  choices: CardSelectChoices,
  query: string,
): { visible: CardChoiceOption[]; hiddenCount: number } => {
  const needle = normalizeForSearch(query.trim());
  if (needle === '') {
    const visible = (
      choices.featured.length > 0 ? choices.featured : choices.options
    ).slice(0, AI_BUILDER_CARD_VISIBLE_OPTIONS);
    return { visible, hiddenCount: choices.options.length - visible.length };
  }
  const matches = choices.options.filter((option) =>
    normalizeForSearch(option.label).includes(needle),
  );
  const visible = matches.slice(0, AI_BUILDER_CARD_VISIBLE_OPTIONS);
  return { visible, hiddenCount: matches.length - visible.length };
};

type ConversationMessage = {
  userType: 'user' | 'bot';
  content: string;
  contentParams?: Record<string, string>;
  answeredChoice?: string;
  narration?: boolean;
};

/**
 * Bot lines that answer no turn: a narration comes before the answer, and
 * "busy" refuses the turn while the lost one still runs
 */
const isNotAnAnswer = (message: ConversationMessage): boolean =>
  message.narration === true ||
  (message.userType === 'bot' &&
    message.content === AiBuilderSystemMessage.flowBusy);

/**
 * The chat ends on something the user said — a typed line, or a choice stored
 * on the question it answers — and no turn in this page is answering it: the
 * page that sent it was gone when the answer came back.
 */
export const isAnswerLost = (
  messages: ConversationMessage[],
  isTurnInFlight: boolean,
): boolean => {
  if (isTurnInFlight) return false;
  const lastMessage = [...messages]
    .reverse()
    .find((message) => !isNotAnAnswer(message));
  if (!lastMessage) return false;
  return lastMessage.userType === 'user' || !!lastMessage.answeredChoice;
};

/**
 * The conversation as the model must read it. A choice picked on a card or a
 * button is stored ON the bot message it answers (so the UI can show it in
 * place), but for the model it is a user turn like any other — it is what the
 * user said.
 */
/**
 * How many messages travel with a turn. The socket schema rejects a context
 * longer than AI_BUILDER_CONTEXT_HARD_CAP — every turn of a long session would
 * then fail validation with nothing to show for it — and the server replays
 * only its own last 20 anyway, so a bigger window buys nothing.
 */
const MAX_SENT_MESSAGES = 40;

/**
 * At most `max` messages: the first USER message, then the tail. The server
 * reads the original intent from the first user message it receives, so
 * dropping it — from what travels or from what a reload restores — would
 * silently rewrite what the whole build was asked to do.
 */
export const windowKeepingIntent = <T extends { userType: 'user' | 'bot' }>(
  messages: T[],
  max: number,
): T[] => {
  if (messages.length <= max) return messages;
  const tail = messages.slice(-max);
  const firstUserMessage = messages.find(
    (message) => message.userType === 'user',
  );
  return firstUserMessage && !tail.includes(firstUserMessage)
    ? [firstUserMessage, ...tail.slice(1)]
    : tail;
};

export const toChatContext = (
  messages: ConversationMessage[],
): { role: 'user' | 'assistant'; content: string }[] =>
  windowKeepingIntent(messages, MAX_SENT_MESSAGES).flatMap((message) => {
    const own = {
      role:
        message.userType === 'user'
          ? ('user' as const)
          : ('assistant' as const),
      // The model reads what was said, not a key: a phrased question rides
      // as a param, and a bare key would leave "{question}" in its place
      content: message.contentParams
        ? interpolateMissingIcuParams(message.content, message.contentParams)
        : message.content,
    };
    return message.answeredChoice
      ? [own, { role: 'user' as const, content: message.answeredChoice }]
      : [own];
  });
